/* eslint-disable no-undef */
import React, { useState, useEffect } from 'react';
import DashboardLayout from '../dashboard/DashboardLayout';
import SettingsWizard from '../../components/SettingsWizard';
import { checkWizardCompletion } from '../../utils/wizardUtils';
import { 
  Trash2, 
  ExternalLink as ExternalLinkIcon, 
  Plus, 
  Sparkles, 
  Clock, 
  Link2, 
  Zap, 
  Building2, 
  CheckCircle2, 
  MoreVertical,
  Target,
  Globe
} from 'lucide-react';
import axiosInstance from '../../utils/axiosInstance';
import { trackPageView, trackButtonClick } from '../../utils/analytics';

const ExternalApply = () => {
  const [externalApplies, setExternalApplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStatus, setWizardStatus] = useState({ completed: false });

  useEffect(() => {
    trackPageView("/external-apply", "External Apply Tracker");
    loadExternalApplies();
    checkWizardStatus();
  }, []);

  const checkWizardStatus = async () => {
    try {
      const completion = await checkWizardCompletion();
      setWizardStatus(completion);
    } catch (e) {
      console.error('Failed to check wizard status:', e);
    }
  };

  const handleWizardComplete = () => {
    localStorage.setItem('applyease_wizard_completed', 'true');
    setShowWizard(false);
    checkWizardStatus();
    loadExternalApplies();
  };

  const loadExternalApplies = async () => {
    setLoading(true);
    try {
      if (typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.sendMessage) {
        chrome.runtime.sendMessage(
          { action: 'getExternalApplyData' },
          (response) => {
            if (chrome.runtime.lastError) loadFromLocalStorage();
            else {
              setExternalApplies(response?.data || []);
              setLoading(false);
            }
          }
        );
      } else loadFromLocalStorage();
    } catch (error) {
      loadFromLocalStorage();
    }
  };

  const loadFromLocalStorage = () => {
    try {
      const stored = localStorage.getItem('applyease_external_apply_data');
      setExternalApplies(stored ? JSON.parse(stored) : []);
    } catch {
      setExternalApplies([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (index) => {
    if (!window.confirm('Purge this tactical link?')) return;
    const updated = externalApplies.filter((_, i) => i !== index);
    setExternalApplies(updated);
    if (typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.sendMessage) {
      chrome.runtime.sendMessage({ action: 'setExternalApplyData', data: updated });
    }
    localStorage.setItem('applyease_external_apply_data', JSON.stringify(updated));
  };

  const handleRemoveAll = () => {
    if (!window.confirm('Wipe all tactical data? This action is irreversible.')) return;
    trackButtonClick("Remove All", "External Apply");
    setExternalApplies([]);
    if (typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.sendMessage) {
      chrome.runtime.sendMessage({ action: 'setExternalApplyData', data: [] });
    }
    localStorage.removeItem('applyease_external_apply_data');
  };

  const handleOpenAll = () => {
    trackButtonClick("Open All", "External Apply");
    externalApplies.forEach((item) => {
      if (item.link) window.open(item.link, '_blank');
    });
  };

  const handleAddToTracker = async (item) => {
    trackButtonClick("Add to Tracker", "External Apply");
    try {
      await axiosInstance.post('/jobs', {
        company: item.companyName || 'Unknown',
        title: item.title || 'Untitled Job',
        url: item.link || '',
        source: 'external',
        status: 'saved',
        notes: 'Captured via External Link Tracker',
      });
      alert('Mission saved to pipeline!');
    } catch (error) {
      alert('Sync failed.');
    }
  };

  if (showWizard) {
    return (
      <DashboardLayout activeTab="external-apply">
        <SettingsWizard onComplete={handleWizardComplete} />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout activeTab="external-apply">
      <div className="max-w-7xl mx-auto space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-20">
        
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
           <div className="flex flex-col text-center md:text-left">
              <h2 className="text-3xl font-black text-slate-900 tracking-tight italic flex items-center justify-center md:justify-start gap-3">
                 <Link2 size={28} className="text-blue-600" /> External Links
              </h2>
              <div className="flex items-center justify-center md:justify-start gap-2 mt-1">
                 <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                 <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{externalApplies.length} External Assets Captured</span>
              </div>
           </div>

           <button
             onClick={() => setShowWizard(true)}
             className="px-8 py-3 bg-slate-900 text-white rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-black hover:shadow-2xl hover:shadow-slate-200 transition-all flex items-center gap-2 italic active:scale-95 shadow-xl shadow-slate-100"
           >
             <Sparkles size={16} className="fill-white" /> Launch Setup Assistant
           </button>
        </div>

        <div className="bg-white border border-slate-200 rounded-[3rem] p-10 shadow-sm relative overflow-hidden group">
           <div className="absolute top-0 right-0 p-12 opacity-5 pointer-events-none -rotate-12 group-hover:rotate-0 transition-transform duration-1000">
              <Globe size={180} className="text-blue-500" />
           </div>

           <div className="flex flex-col lg:flex-row justify-between items-center gap-10 mb-12 relative z-10">
              <div className="flex-1">
                 <h3 className="text-xl font-black text-slate-900 italic mb-2">Off-Platform Missions</h3>
                 <p className="text-xs text-slate-500 font-medium leading-relaxed max-w-xl italic">
                    The system has captured these off-platform job opportunities. These require manual application and tracking.
                 </p>
              </div>
              <div className="flex flex-wrap gap-4 w-full md:w-auto">
                 <button 
                     onClick={handleOpenAll}
                     disabled={externalApplies.length === 0}
                     className="flex-1 md:flex-none px-6 py-3 bg-slate-900 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-black transition-all flex items-center justify-center gap-2 italic disabled:opacity-30"
                 >
                    <Zap size={14} className="fill-white"/> Deploy All
                 </button>
                 <button 
                     onClick={handleRemoveAll}
                     disabled={externalApplies.length === 0}
                     className="flex-1 md:flex-none px-6 py-3 bg-white border-2 border-rose-100 text-rose-500 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-rose-50 transition-all flex items-center justify-center gap-2 italic disabled:opacity-30"
                 >
                    <Trash2 size={14}/> Wipe Intelligence
                 </button>
              </div>
           </div>

           {externalApplies.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-32 border-2 border-dashed border-slate-100 rounded-[2.5rem] bg-slate-50/50">
                 <div className="w-20 h-20 bg-slate-100 rounded-3xl flex items-center justify-center text-slate-300 mb-6 shadow-inner">
                    <Target size={40} />
                 </div>
                 <h4 className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] italic">No External Targets Identified</h4>
                 <p className="text-[10px] text-slate-400 mt-2 font-bold uppercase tracking-tight">Activate extension during browsing to capture tactical assets</p>
              </div>
           ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 relative z-10">
                 {externalApplies.map((item, index) => (
                    <div key={index} className="bg-white border border-slate-200 rounded-[2.5rem] p-8 hover:border-blue-400 hover:shadow-2xl hover:shadow-blue-500/5 transition-all duration-300 group flex flex-col">
                       <div className="flex items-start justify-between mb-6">
                          <div className="w-14 h-14 bg-slate-50 border border-slate-100 rounded-[1.5rem] flex items-center justify-center text-slate-400 font-black italic shadow-inner group-hover:bg-blue-600 group-hover:text-white group-hover:border-blue-600 transition-all">
                             {item.companyName?.slice(0, 1) || '?'}
                          </div>
                          <button onClick={() => handleRemove(index)} className="p-2 text-slate-300 hover:text-rose-500 transition-colors opacity-0 group-hover:opacity-100"><Trash2 size={18}/></button>
                       </div>

                       <div className="space-y-1 mb-8">
                          <h4 className="text-lg font-black text-slate-900 italic line-clamp-2 leading-tight tracking-tight group-hover:text-blue-600 transition-colors">{item.title || 'Unknown Objective'}</h4>
                          <div className="flex items-center gap-2">
                             <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{item.companyName || 'Classified Origin'}</span>
                          </div>
                       </div>

                       <div className="mt-auto space-y-4 pt-6 border-t border-slate-50">
                          <div className="flex items-center gap-2 text-slate-400">
                             <Clock size={12} />
                             <span className="text-[9px] font-black uppercase tracking-widest">{item.time ? new Date(item.time).toLocaleDateString() : 'Capture Time Unknown'}</span>
                          </div>
                          <div className="flex gap-2">
                             <button 
                                onClick={() => handleAddToTracker(item)}
                                className="flex-1 py-3 bg-blue-50 text-blue-600 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-blue-600 hover:text-white transition-all italic flex items-center justify-center gap-2"
                             >
                                <Target size={14}/> Pipeline
                             </button>
                             {item.link && (
                                <a 
                                   href={item.link} 
                                   target="_blank" 
                                   rel="noreferrer"
                                   className="flex-1 py-3 bg-slate-50 text-slate-600 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-900 hover:text-white transition-all italic flex items-center justify-center gap-2"
                                >
                                   <ExternalLinkIcon size={14}/> Visit
                                </a>
                             )}
                          </div>
                       </div>
                    </div>
                 ))}
              </div>
           )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ExternalApply;
